import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, SimpleChanges } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { ScrollService } from 'app/core/services/scroll.service';
import { ValidatorService } from 'app/core/services/validator.service';
import { FlowDetail } from 'app/core/store/edit-florder-detail/florder-detail.interface';
import { CheckFlowDateResponse } from 'app/core/store/edit-flow-detail/flow-detail.interface';
import { FlorderEditPlanningComponent } from 'app/modules/florder/florder-edit-planning/florder-edit-planning.component';
import moment from 'moment';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import { appSettings } from '../../../appSettings';
import { FlowSandbox } from '../../../core/sandboxes/flow.sandbox';
import { DatesRange, excludeFuture } from '../../../core/util/date.utils';
import { util } from '../../../core/util/util';

@Component({
  selector: 'app-flow-edit-planning',
  templateUrl: './flow-edit-planning.component.html',
  styleUrls: ['../../florder/florder-edit-planning/florder-edit-planning.component.scss', './flow-edit-planning.component.scss'],
})
export class FlowEditPlanningComponent extends FlorderEditPlanningComponent implements OnChanges {
  @Input() datesRange: DatesRange;
  @Input() flowDate: CheckFlowDateResponse;
  @Input() flowDetail: FlowDetail;
  @Input() frequency: 'T' | 'W' | 'D';
  @Input() triggerValidation: EventEmitter<boolean>;
  flowDateValidating = false;
  labelWidth = appSettings.LABEL_WIDTH;
  private triggerValidationSubscription: any;

  showDate = true;

  private _dateRanges?: DatesRange[];
  get dateRanges(): DatesRange[] {
    if (this._dateRanges === undefined) {
      this.calculateDatesRange();
    }
    return this._dateRanges;
  }

  constructor(
    formBuilder: UntypedFormBuilder,
    validatorService: ValidatorService,
    scrollService: ScrollService,
    private translate: TranslateService,
    private flowSandbox: FlowSandbox,
    private cd: ChangeDetectorRef,
  ) {
    super(formBuilder, validatorService, scrollService);
  }

  ngOnChanges(changes: SimpleChanges) {
    super.ngOnChanges(changes);
    if (changes.triggerValidation) {
      if (this.triggerValidationSubscription) {
        this.triggerValidationSubscription.unsubscribe();
      }
      this.triggerValidationSubscription = this.triggerValidation.subscribe(() => {
        const flowDateControl = this.editFlorderPlanningForm.get('flowDate');
        const flowDate = flowDateControl.value;
        this.editFlorderPlanningForm.updateValueAndValidity();

        if (flowDate) {
          flowDateControl.setValue(flowDate);
        }
      });
    }

    if (changes.flowDetail && this.editFlorderPlanningForm) {
      this.editFlorderPlanningForm.updateValueAndValidity();
      if (this.flowDetail.flowDateError) {
        this.editFlorderPlanningForm.setErrors({
          invalid: this.flowDetail.flowDateError,
        });
      } else {
        this.editFlorderPlanningForm.setErrors(null);
      }
    }

    if (changes.frequency || changes.datesRange) {
      this._dateRanges = undefined;
    }
  }

  createForm() {
    const controlsConfig = {
      flowDate: [this.flowDetail.planning.flowDate, [Validators.required]],
      transferDate: [this.flowDetail.planning.transferDate],
      definitiveDate: [this.flowDetail.planning.definitiveDate],
    };
    const initialFlowDate = this.flowDetail.planning.flowDate; // store initial value to filter out date-changes that are not really changes
    this.editFlorderPlanningForm = this.formBuilder.group(controlsConfig);
    this.formValidChanged.emit(this.editFlorderPlanningForm.valid);

    // add async validation on flowDate. not using AsyncValidator because it triggers an error equal to
    this.editFlorderPlanningForm.get('flowDate').valueChanges
      .pipe(
        map((value: moment.Moment | string) => util.formatDate(value)),
        filter(flowDate => flowDate !== initialFlowDate), // if date didn't change against initial value (update flow), do not validate
        distinctUntilChanged(),
      )
      .subscribe(async (flowDate) => {
        const fromId = this.flowDetail.deliveryMethod.from.id;
        const toId = this.flowDetail.deliveryMethod.to.id;
        const frequency = this.frequency;
        const flowWeek = {
          weekNumber: util.getWeekNumber(util.parseDateString(flowDate)),
          year: util.parseDateString(flowDate).year,
        };
        try {
          this.flowDateValidating = true;
          await this.flowSandbox.checkFlowDate(flowDate, frequency, fromId, toId, flowWeek);
          // if it succeeds, then date is ok
          this.editFlorderPlanningForm.get('flowDate').setErrors(null);
        } catch (error) {
          if (!error.error?.Message) {
            console.error('Error object not as expected', error);
          }
          const flowDateError = error.error?.Message || 'Invalid date';
          this.editFlorderPlanningForm.get('flowDate').setErrors({ [flowDateError]: true });
          setTimeout(() => { this.cd.markForCheck() });
        } finally {
          this.flowDateValidating = false;
        }
      });

    this.editFlorderPlanningForm.statusChanges
      .pipe(
        map(data => this.editFlorderPlanningForm.valid),
        distinctUntilChanged()
      )
      .subscribe(valid => {
        this.formValidChanged.emit(valid);
      });
  }

  private calculateDatesRange() {
    if (this.frequency) {
      if (this.datesRange) {
        if (this.frequency === 'T') {
          this._dateRanges = [this.datesRange];
        } else {
          this._dateRanges = excludeFuture([this.datesRange]);
        }
      } else {
        if (this.frequency === 'T') {
          this._dateRanges = [];
        } else {
          this._dateRanges = excludeFuture([]);
        }
      }
    }
  }
}
